import { useCallback, useEffect, useRef, useState } from 'react';
import axios from 'axios';
import i18n from '~/i18n';
import { PageIdRequestV2 } from '~/api-services/ReportsService/types';
import { joinCsvLines } from '~/lib/csv';
import { collectCsvLines, ExportColumn, ExportContext, ExportPage } from '~/lib/reportExportCollector';
import { downloadTextFile } from '~/lib/download';

export const EXPORT_PAGE_SIZE = 500;
export const EXPORT_MAX_ROWS = 100000;

const ASSEMBLE_CHUNK = 5000;

export type { ExportColumn, ExportContext, ExportPage };

export type ExportState = 'idle' | 'running' | 'done' | 'error' | 'cancelled' | 'truncated';

type UseReportExportArgs<T> = {
    enabled: boolean;
    fetchPage: (lastPageId: PageIdRequestV2 | null, pageSize: number, signal: AbortSignal) => Promise<ExportPage<T>>;
    columns: ExportColumn<T>[];
    context: ExportContext;
    filename: () => string;
    pageSize?: number;
    maxRows?: number;
};

const yieldToUI = () => new Promise<void>((resolve) => setTimeout(resolve, 0));

const isCancellation = (e: any) => axios.isCancel?.(e) || e?.name === 'AbortError' || e?.code === 'ERR_CANCELED';

export const useReportExport = <T>({
    enabled,
    fetchPage,
    columns,
    context,
    filename,
    pageSize = EXPORT_PAGE_SIZE,
    maxRows = EXPORT_MAX_ROWS,
}: UseReportExportArgs<T>) => {
    const [state, setState] = useState<ExportState>('idle');
    const [rowsFetched, setRowsFetched] = useState(0);
    const [error, setError] = useState<string | null>(null);
    const abortRef = useRef<AbortController | null>(null);
    const runningRef = useRef(false);

    useEffect(() => () => abortRef.current?.abort(), []);

    const reset = useCallback(() => {
        setState('idle');
        setRowsFetched(0);
        setError(null);
    }, []);

    const cancel = useCallback(() => abortRef.current?.abort(), []);

    const start = useCallback(async () => {
        if (!enabled || runningRef.current) {
            return;
        }
        runningRef.current = true;

        const abort = new AbortController();
        abortRef.current = abort;

        setState('running');
        setRowsFetched(0);
        setError(null);

        try {
            const { lines, truncated } = await collectCsvLines<T>({
                fetchPage,
                columns,
                context,
                headers: columns.map((c) => i18n.t(c.header)),
                pageSize,
                maxRows,
                signal: abort.signal,
                onProgress: setRowsFetched,
                onPageDone: yieldToUI,
            });

            const chunks: string[] = [];
            for (let i = 0; i < lines.length; i += ASSEMBLE_CHUNK) {
                chunks.push(joinCsvLines(lines.slice(i, i + ASSEMBLE_CHUNK)));
                await yieldToUI();
            }

            await downloadTextFile(filename(), chunks.join(''));
            setState(truncated ? 'truncated' : 'done');
        } catch (e: any) {
            if (isCancellation(e)) {
                setState('cancelled');
            } else {
                console.log('export error', e);
                setError(e?.message ?? 'export-failed');
                setState('error');
            }
        } finally {
            runningRef.current = false;
            abortRef.current = null;
        }
    }, [enabled, fetchPage, columns, context, filename, pageSize, maxRows]);

    return { state, rowsFetched, error, start, cancel, reset };
};
